import React, { useContext, useState } from 'react'
import Typography from '../../../../Typography'
import { currencyFormatter } from '../../../../../utils/functions'
import Button from '../../../../Button'
import styles from '../YearbookForm/YearbookForm.module.sass'
import { Cart, OrderPackage } from '../../../../../models'
import TextInput from '../../../../TextInput'
import { useHistory } from 'react-router-dom'
import { CustomerContext } from '../../../../../contexts/CustomerContext'
import routes from '../../../../../routes'

const NAME_ON_COVER_PRICE = 5

function getInitialEmail(student, customer) {
  if (student.email) return student.email
  if (customer && customer.email) return customer.email

  return ''
}

// cutoff_date arrives as 'YYYY-MM-DD'; parse the parts so it isn't shifted a
// day by UTC interpretation of a bare date string.
function formatCutoff(iso) {
  if (!iso) return null

  const [year, month, day] = iso.split('-').map(Number)

  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  })
}

// The ship-to-home variant of the yearbook order form, shown once a school's
// ship-to-school price window has expired. Price, option and shipping come from
// the home-delivery API (the expired package carries no current prices), and
// the order package is flagged ship_to_mail so the order ships to the home
// address collected at checkout.
const YearbookHomeForm = ({ student, school, homeDelivery, messageId }) => {
  const history = useHistory()
  const { customer, setCustomer } = useContext(CustomerContext)

  const hasInitialEmail = Boolean(student.email || (customer && customer.email))
  const [email, setEmail] = useState(getInitialEmail(student, customer))

  const allowPersonalization = !school.noPersonalization
  const [includeName, setIncludeName] = useState(allowPersonalization)

  const pricePerYearbook = Number(homeDelivery.package.home_price) || 0
  const shippingFee = Number(homeDelivery.shipping_fee) || 0
  const cutoff = formatCutoff(homeDelivery.cutoff_date)

  const total = pricePerYearbook + (includeName ? NAME_ON_COVER_PRICE : 0) + shippingFee

  const myCart = async () => {
    if (customer && customer.cart) {
      customer.cart.school = school

      const initializedCart = new Cart(customer.cart)
      initializedCart.isPersisted = true
      await initializedCart.save()

      initializedCart.email = email
      initializedCart.messageId = messageId
      await initializedCart.save()

      return initializedCart
    }

    return new Cart({ email, schoolId: student.schoolId, messageId })
  }

  const handleSubmit = async (evt) => {
    evt.preventDefault()

    const cart = await myCart()

    cart.orderPackages = [new OrderPackage({
      optionId: homeDelivery.package.option_id,
      price: pricePerYearbook,
      packageId: homeDelivery.package.id,
      hasNameOnCover: includeName,
      studentId: student.id,
      shipDestination: 'ship_to_mail',
    })]

    cart.save({ with: 'orderPackages' }).then(() => {
      setCustomer({
        ...customer, email, priorityCart: cart, currentStudent: student, currentSchool: school,
      })
      history.push(routes.review())
    })
  }

  return (
    <form className={styles.form} onSubmit={handleSubmit}>
      <div>
        <label><Typography weight="bold">{student.fullName(true)}</Typography></label>
      </div>
      <div>
        <label><Typography weight="bold">{school.name}</Typography></label>
      </div>
      <div>
        <label style={{ display: 'flex', gap: '.5rem' }}>
          <Typography>Price per yearbook:</Typography>
          <Typography weight="bold">{currencyFormatter(pricePerYearbook)}</Typography>
        </label>
      </div>
      <div>
        <label style={{ display: 'flex', gap: '.5rem' }}>
          <Typography>Home shipping:</Typography>
          <Typography weight="bold">{currencyFormatter(shippingFee)}</Typography>
        </label>
      </div>
      <div>
        {!hasInitialEmail && (
          <label className={styles.subrow}>
            <Typography>Email:</Typography>
            <TextInput required type="email" val={email} onChange={(evt) => setEmail(evt.target.value)} />
          </label>
        )}
        {allowPersonalization && (
          <div className={styles.box}>
            <span>Customization (Optional):</span>
            <label>
              <input type="checkbox" checked={includeName} value={includeName} onChange={() => setIncludeName((prev) => !prev)} />
              <span>Name & Photo on Cover <span className={styles.namePricing}>($5.00)</span></span>
            </label>
          </div>
        )}
        <label style={{ display: 'flex', justifyContent: 'flex-end', margin: '1.5rem 0' }}>
          <Typography>Subtotal:</Typography>
          <Typography weight="bold">{currencyFormatter(total)}</Typography>
        </label>
      </div>
      {cutoff && (
        <Typography align="center" fontStyle="italic">Order by {cutoff}.</Typography>
      )}
      <Button fullWidth variant="primary" circular type="submit">Proceed to Checkout</Button>
    </form>
  )
}

export default YearbookHomeForm
